import { SalesType } from '@client/new/common/types';

import {
  DispatcherResult,
  OptimisticState,
  ProgramItem,
} from './programs.types';

/**
 * Pure reducer-style helpers for the Programs page reorder overlay.
 *
 * None of these functions depend on React, Apollo, or any I/O. They are
 * testable in isolation and live here (rather than inline in the hook) so
 * the hook stays small and the optimistic logic is independently
 * verifiable.
 *
 * Design notes:
 * - Each reorder request gets a unique `reqId`. The id embeds a millisecond
 *   timestamp as its prefix (see `buildReqId`) so a janitor can prune stale
 *   settled entries without needing a separate timestamp map.
 * - Rollback on error is keyed by request id, NOT by React closure. When a
 *   request fails we only clear the overlay for that `salesType` if no
 *   newer in-flight reorder exists for the same bucket. This avoids the
 *   stale-closure rollback bug from the abandoned pr1944 branch.
 */

// ---------------------------------------------------------------------------
// Request id
// ---------------------------------------------------------------------------

/**
 * Builds a reorder request id with the form `"<timestamp>:<random>"`. The
 * timestamp prefix lets `clearStaleInflight` prune settled entries by age.
 *
 * `crypto.randomUUID()` is preferred for the random part, with a fallback
 * that works in test environments that lack it (jsdom / happy-dom).
 */
export const buildReqId = (nowMs: number): string => {
  const random =
    typeof globalThis.crypto !== 'undefined' &&
    typeof globalThis.crypto.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `${Math.random().toString(36).slice(2)}${Math.random()
          .toString(36)
          .slice(2)}`;
  return `${nowMs}:${random}`;
};

const extractTimestamp = (reqId: string): number | null => {
  const [prefix] = reqId.split(':');
  const n = Number(prefix);
  return Number.isFinite(n) && prefix.length > 0 ? n : null;
};

// ---------------------------------------------------------------------------
// State constructors
// ---------------------------------------------------------------------------

export const emptyOptimisticState = (): OptimisticState => ({
  order: {},
  inflight: {},
  reqSalesType: {},
  orderOwner: {},
});

// ---------------------------------------------------------------------------
// startInflight
// ---------------------------------------------------------------------------

export type StartInflightPayload = {
  reqId: string;
  salesType: SalesType;
  ids: Array<number>;
};

export const startInflight = (
  state: OptimisticState,
  payload: StartInflightPayload,
): OptimisticState => ({
  order: { ...state.order, [payload.salesType]: payload.ids },
  inflight: { ...state.inflight, [payload.reqId]: { type: 'fetching' } },
  reqSalesType: { ...state.reqSalesType, [payload.reqId]: payload.salesType },
  orderOwner: { ...state.orderOwner, [payload.salesType]: payload.reqId },
});

// ---------------------------------------------------------------------------
// settleInflight
// ---------------------------------------------------------------------------

export type SettleInflightPayload = {
  reqId: string;
  salesType: SalesType;
  result: DispatcherResult;
};

const toSettledEntry = (
  result: DispatcherResult,
): OptimisticState['inflight'][string] => {
  if ('message' in result)
    return { type: 'fetch-error', value: result.message };
  return { type: 'fetched', value: null };
};

export const settleInflight = (
  state: OptimisticState,
  payload: SettleInflightPayload,
): OptimisticState => {
  const settledEntry: OptimisticState['inflight'][string] = toSettledEntry(
    payload.result,
  );
  const nextInflight: OptimisticState['inflight'] = {
    ...state.inflight,
    [payload.reqId]: settledEntry,
  };

  // Success: keep the overlay; post-refetch cleanup (`dropOverlayOnRefresh`)
  // removes it after the server state is back in sync.
  if (payload.result.ok) {
    return { ...state, inflight: nextInflight };
  }

  // Error: only roll back the overlay when this request still owns it.
  // A newer reorder (success or fetching) in the same bucket will have
  // taken ownership via `startInflight`; in that case leave the overlay
  // alone so req-a's rollback does not clobber req-b's intent.
  if (state.orderOwner[payload.salesType] !== payload.reqId) {
    return { ...state, inflight: nextInflight };
  }

  const { [payload.salesType]: _removed, ...restOrder } = state.order;
  const { [payload.salesType]: _removedOwner, ...restOwner } = state.orderOwner;
  return {
    ...state,
    order: restOrder,
    orderOwner: restOwner,
    inflight: nextInflight,
  };
};

// ---------------------------------------------------------------------------
// dropOverlayOnRefresh
// ---------------------------------------------------------------------------

export type DropOverlayOnRefreshPayload = {
  reqId: string;
  salesType: SalesType;
};

/**
 * Called after a post-mutation refetch resolves successfully. Drops the
 * bucket's overlay only when this request still owns it -- a newer reorder
 * dispatched mid-refetch would have taken ownership via `startInflight`
 * and must keep its overlay visible until its own refetch lands.
 */
export const dropOverlayOnRefresh = (
  state: OptimisticState,
  payload: DropOverlayOnRefreshPayload,
): OptimisticState => {
  if (state.orderOwner[payload.salesType] !== payload.reqId) return state;
  const { [payload.salesType]: _removed, ...restOrder } = state.order;
  const { [payload.salesType]: _removedOwner, ...restOwner } = state.orderOwner;
  return { ...state, order: restOrder, orderOwner: restOwner };
};

// ---------------------------------------------------------------------------
// clearStaleInflight
// ---------------------------------------------------------------------------

/**
 * Drops settled inflight entries whose embedded timestamp is older than
 * `nowMs - ttlMs`. Entries that are still fetching are always preserved.
 * Entries whose reqId lacks a parseable timestamp are preserved as well;
 * the janitor is intentionally conservative.
 */
export const clearStaleInflight = (
  state: OptimisticState,
  nowMs: number,
  ttlMs: number,
): OptimisticState => {
  const cutoff = nowMs - ttlMs;
  const keep = ([id, status]: [
    string,
    OptimisticState['inflight'][string],
  ]) => {
    if (status.type === 'fetching') return true;
    const ts = extractTimestamp(id);
    if (ts === null) return true;
    return ts > cutoff;
  };

  const nextInflight = Object.fromEntries(
    Object.entries(state.inflight).filter(keep),
  );
  const nextMeta = Object.fromEntries(
    Object.entries(state.reqSalesType).filter(([id]) =>
      Object.prototype.hasOwnProperty.call(nextInflight, id),
    ),
  );
  return { ...state, inflight: nextInflight, reqSalesType: nextMeta };
};

// ---------------------------------------------------------------------------
// applyOptimisticOrder
// ---------------------------------------------------------------------------

/**
 * Produces the user-visible program list by applying any active reorder
 * overlay on top of the server-provided order. Programs in the overlay come
 * first, in overlay order; programs missing from the overlay keep their
 * original server position and are appended after. Overlay ids that no
 * longer match an existing program are dropped silently (they can happen
 * between a drag and a delete).
 */
export const applyOptimisticOrder = (
  programs: Array<ProgramItem>,
  state: OptimisticState,
): Array<ProgramItem> => {
  const hasOverlay = Object.values(state.order).some(
    (overlay) => overlay !== undefined,
  );

  if (!hasOverlay) return programs;

  const byId = new Map(programs.map((p) => [p.id, p]));
  const result: Array<ProgramItem> = [];
  const placed = new Set<number>();

  // 1. Walk the programs in server order. For each program's salesType,
  //    decide whether the overlay dictates its position.
  const bucketsHandled = new Set<SalesType>();
  for (const program of programs) {
    const overlay = state.order[program.salesType];
    if (overlay === undefined) {
      if (!placed.has(program.id)) {
        result.push(program);
        placed.add(program.id);
      }
      continue;
    }

    // For buckets with an overlay, lay down the overlay order the first
    // time we encounter any program from that bucket, then skip the rest.
    if (!bucketsHandled.has(program.salesType)) {
      bucketsHandled.add(program.salesType);
      for (const id of overlay) {
        const match = byId.get(id);
        if (match && match.salesType === program.salesType) {
          result.push(match);
          placed.add(id);
        }
      }
      // After the overlay, append bucket programs that weren't referenced.
      for (const p of programs) {
        if (p.salesType === program.salesType && !placed.has(p.id)) {
          result.push(p);
          placed.add(p.id);
        }
      }
    }
  }

  return result;
};
